import { useCallback, useMemo, useState, useEffect } from "react";
import type { PaginationModeContextValue, PaginationModeState, PaginationConfig } from "../../contexts/types";

/**
 * Default pagination configuration
 * Can be overridden per entity if needed
 */
const DEFAULT_PAGINATION_CONFIG: PaginationConfig = {
  availablePageSizes: [10, 25, 50, 100, 200],
  defaultPageSize: 10,
  infiniteScrollThreshold: 50, // pixels from bottom
  infiniteScrollDebounce: 500, // milliseconds
};

/**
 * Composition hook for Pagination Mode State
 * Manages pagination and infinite scroll state with localStorage persistence
 *
 * Job Duty: Manage pagination and infinite scroll state
 */
export function useCompositionPagination(
  entityName: string,
  defaultPageSize?: number
): PaginationModeContextValue {
  // Memoised on the only input that can change it. Built inline, this object was
  // a NEW reference on every render, and it is a dependency of the `useMemo`
  // below — so the hook handed back a new `PaginationModeContextValue` every
  // time the entity page rendered, even when nothing had changed. That value is
  // the pagination context AND a dependency of the board-view override memo in
  // `useUniversalEntityPageComposition`, so the churn propagated to every
  // consumer: precisely what those memos exist to prevent (MEMO_STABLE_PROPS).
  const finalPaginationConfig: PaginationConfig = useMemo(
    () => ({
      ...DEFAULT_PAGINATION_CONFIG,
      defaultPageSize: defaultPageSize || DEFAULT_PAGINATION_CONFIG.defaultPageSize,
    }),
    [defaultPageSize],
  );

  const infiniteScrollStorageKey = `${entityName}-infinite-scroll`;

  // Initialize pagination mode state
  const [paginationMode, setPaginationModeState] = useState<PaginationModeState>({
    mode: "pagination",
    page: 0,
    pageSize: finalPaginationConfig.defaultPageSize,
    cursor: null,
  });

  // Initialize infinite scroll preference from localStorage on mount
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedInfiniteScroll = localStorage.getItem(infiniteScrollStorageKey);
      if (savedInfiniteScroll === "true") {
        setPaginationModeState((prev: PaginationModeState) => ({
          ...prev,
          mode: "infinite-scroll",
        }));
      }
    }
  }, [infiniteScrollStorageKey]);

  // Set current page
  const setPage = useCallback((page: number) => {
    setPaginationModeState((prev: PaginationModeState) => ({
      ...prev,
      page,
    }));
  }, []);

  // Set page size
  const setPageSize = useCallback((pageSize: number) => {
    setPaginationModeState((prev: PaginationModeState) => ({
      ...prev,
      pageSize,
      page: 0, // Reset to first page when page size changes
    }));
  }, []);

  // Set cursor for infinite scroll
  const setCursor = useCallback((cursor: string | null) => {
    setPaginationModeState((prev: PaginationModeState) => ({
      ...prev,
      cursor,
    }));
  }, []);

  // Set page info (for cursor-based pagination)
  const setPageInfo = useCallback((pageInfo: PaginationModeState["pageInfo"]) => {
    setPaginationModeState((prev: PaginationModeState) => ({
      ...prev,
      pageInfo,
    }));
  }, []);

  // Set total count
  const setTotalCount = useCallback((totalCount: number | undefined) => {
    setPaginationModeState((prev: PaginationModeState) => ({
      ...prev,
      totalCount,
    }));
  }, []);

  // Toggle between pagination and infinite scroll modes
  const toggleInfiniteScroll = useCallback(
    (enabled: boolean) => {
      setPaginationModeState((prev: PaginationModeState) => ({
        ...prev,
        mode: enabled ? "infinite-scroll" : "pagination",
        page: 0, // Reset to first page when switching modes
        cursor: null, // Clear cursor when switching modes
      }));

      // Persist preference to localStorage
      if (typeof window !== "undefined") {
        localStorage.setItem(infiniteScrollStorageKey, String(enabled));
      }
    },
    [infiniteScrollStorageKey]
  );

  // Reset pagination to initial state
  const resetPagination = useCallback(() => {
    setPaginationModeState({
      mode: "pagination",
      page: 0,
      pageSize: finalPaginationConfig.defaultPageSize,
      cursor: null,
    });
  }, [finalPaginationConfig.defaultPageSize]);

  // Assemble pagination mode context value
  return useMemo(
    () => ({
      paginationMode,
      setPage,
      setPageSize,
      setCursor,
      setPageInfo,
      setTotalCount,
      toggleInfiniteScroll,
      resetPagination,
      config: finalPaginationConfig,
    }),
    [paginationMode, setPage, setPageSize, setCursor, setPageInfo, setTotalCount, toggleInfiniteScroll, resetPagination, finalPaginationConfig]
  );
}
